import React, {
  useState, useMemo, useCallback, useEffect, useContext,
} from "react";
import { toast } from "react-toastify";
import {
  Button,
  Tooltip,
  Checkbox,
  Divider,
} from "antd";
import { toPairs } from "lodash";
import InstrumentModal from "./InstrumentModal";
import EditResultModal from "./EditResultModal";
import DfLabelModal from "./DfLabelModal";
import GenistaIDList from "./GenistaIDList";
import MrlCellContent from "./MrlCellContent";
import { ChemBioPendingContext, PendingTableCellContext } from "../../ChemBioPendingContext";
import { getUser } from "../../../../../utils/helpers";

const DefaultModalData = { open: false, modalTest: null, selectedIds: [] };
const PendingTableCell = ({ cellItem, data }) => {
  const {
    chemDropdown,
    helperFn,
    generatePendingQueue,
    modalMap,
    sampleStatusMap,
    mrlInstruments,
    enableExtractedColumn,
    handleExtractedCheckboxOnChange,
    selectedGenerate,
    instrumentMap,
    mrlTestSet,
  } = useContext(ChemBioPendingContext);

  const [sampleMap, setSampleMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [seqCheckedCount, setSeqCheckedCount] = useState(0);
  const [instrumentModalData, setInstrumentModalData] = useState(DefaultModalData);
  const [editResultModalData, setEditResultModalData] = useState(DefaultModalData);
  const [dfModalData, setDfModalData] = useState(DefaultModalData);
  const {
    param, sample_status, isGenistaCol,
  } = cellItem;
  // const dfInstrumentList = [1, 2, 4, 5]; // 1: HPLC, GC: 2, GC-MS: 4, LC-MS: 5
  // const validDfInstrument = dfInstrumentList.find((instrumentId) => instrumentId === data.instrument_id);
  const chemList = sampleStatusMap?.get(data.Test)?.get(sample_status) ?? [];
  const testList = chemDropdown ? chemList : data[param];
  // const multiSelect = useMemo(() => (sample_status === "analyze" || (sample_status === "extract" && validDfInstrument)) && testList.length > 0, [sample_status, testList, validDfInstrument]);
  const multiSelect = sample_status === "analyze" && testList.length > 0;
  const extractedColumnMultiSelect = (sample_status === "extract" || sample_status === "load") && testList.length > 0 && data.generate_load_excel;
  const useLoadedMrlLayout = useMemo(() => sample_status === "load" && mrlTestSet.has(data.Test) && mrlInstruments.length > 0, [sample_status, data, mrlInstruments, mrlTestSet]);

  useEffect(() => {
    if (!selectedGenerate.has(sample_status) || selectedGenerate.get(sample_status).size === 0) { // when header checkbox select all unchecks
      setSeqCheckedCount(0);
    } else if (selectedGenerate.get(sample_status).size === selectedGenerate.get(`${sample_status}Count`)) { // when header checkbox select all checks
      setSeqCheckedCount(testList.length);
    }
  }, [selectedGenerate, testList, sample_status]);

  const tooltipTitle = useCallback((element) => {
    const baseInfo = [
      { label: "Work order", param: "order_id" },
      { label: "Assigned To", param: "assigned_to" },
      { label: "Sample ID", param: "sample_id" },
      { label: "Sample Type", param: "sample_type" },
      { label: "QC Status", param: "qc_status" },
    ];
    // const showDfLabels = (element.sample_status === "load" || element.sample_status === "analyze") && validDfInstrument;
    // if (showDfLabels) {
    //   baseInfo.push({
    //     label: "Labels", param: "df_labels",
    //   });
    //   element.df_labels = generateLabel({ ...element, test: data.Test });
    // }
    const validInfo = baseInfo.filter((item) => element[item.param]); // Only render a HTML dom if the value is valid

    return (
      <div className="PendingTable__Tooltip__Container">
        {validInfo.map((item, i) => {
          const { label, param: keyParam } = item;
          const value = element[keyParam];
          const childNode = (<span>{value}</span>);
          // if (showDfLabels && keyParam === "df_labels") {
          //   childNode = (
          //     <ul>
          //       {value.map((labelString, j) => <li key={j}>{labelString}</li>)}
          //     </ul>
          //   );
          // }
          return (
            <div className="PendingTable__Tooltip__Content" key={i}>
              <label>{`${label}:`}</label>
              {childNode}
            </div>
          );
        })}
      </div>
    );
  }, []);

  const handleCheckboxOnChange = (e, item) => {
    const { test_in_sample_id } = item;
    const checked = e.target.checked;
    setSampleMap((prev) => {
      const newMap = new Map(prev);
      if (checked) {
        newMap.set(test_in_sample_id, { ...item, test: data.Test });
      } else {
        newMap.delete(test_in_sample_id);
      }
      return newMap;
    });
  };

  const handleSelectAll = (e, list) => {
    const checked = e.target.checked;
    setSampleMap((prev) => {
      const newMap = new Map(prev);
      list.forEach((item) => {
        const { test_in_sample_id } = item;
        if (checked) {
          newMap.set(test_in_sample_id, { ...item, test: data.Test });
        } else {
          newMap.delete(test_in_sample_id);
        }
      });
      return newMap;
    });
  };

  const handleSequenceSelectAll = (e, list) => {
    const checked = e.target.checked;
    handleExtractedCheckboxOnChange(e, list, true); // update the PendingTable state accordingly
    setSeqCheckedCount(checked ? list.length : 0); // update the current Select All checkbox state
  };

  const handleSequenceCheckbox = (e, p) => {
    const checked = e.target.checked;
    handleExtractedCheckboxOnChange(e, p);
    setSeqCheckedCount((prev) => (checked ? prev + 1 : prev - 1));
  };

  const resetCheckboxData = () => {
    setSeqCheckedCount(0);
    setSampleMap(new Map());
  };

  const handleSubmit = async () => {
    setLoading(true);
    const mapValues = Array.from(sampleMap.values()); // Get the list of test id from the selected tests map
    const test_in_sample_details = mapValues.map((item) => ({ id: item.test_in_sample_id, loaded_on_instruments: item.loaded_on_instruments }));
    const payload = {
      test_in_sample_details,
      action: "ready",
      user_id: getUser().id,
    };
    const { message, success } = await helperFn.markTestRunOnInstrument(payload);
    if (success) {
      await generatePendingQueue(null, null, false);
      resetCheckboxData();
      toast.success(message);
    } else {
      toast.error(message);
    }
    setLoading(false);
  };

  const handleOpenDfModal = () => {
    setDfModalData({ open: true, selectedIds: Array.from(sampleMap.values()) });
  };

  const handleSubmitButton = () => {
    if (sample_status === "extract") {
      handleOpenDfModal();
    } else {
      handleSubmit();
    }
  };

  const handleGenistaIdOnClick = (disablePopup, modalTest) => {
    if (disablePopup) {
      return;
    }
    // const enabledDfInput = modalTest.sample_status === "pending" && validDfInstrument;
    const enabledDfInput = false;
    // Check if test category is metals - for metals category tests, show InstrumentModal instead of EditResultModal
    const isMetalsCategory = modalTest.test_category === "Metals";
    if ((modalTest.sample_status === "load" || modalTest.sample_status === "analyze") && !isMetalsCategory) {
      setEditResultModalData({ open: true, modalTest: { ...modalTest } });
    } else {
      setInstrumentModalData({ open: true, modalTest: { ...modalTest, enabledDfInput } });
    }
  };

  const resetAllModalData = () => {
    setInstrumentModalData(DefaultModalData);
    setEditResultModalData(DefaultModalData);
    setDfModalData(DefaultModalData);
  };

  const handleCloseModal = async (refresh, message = "") => {
    if (refresh) {
      await generatePendingQueue(null, null, false);
      if (message) toast.success(message);
    }
    resetAllModalData();
  };

  const dfTooltip = useCallback(() => {
    const instrumentInfo = data.instrument_info;
    if (!instrumentInfo || instrumentMap.size === 0) return "";
    const params = toPairs(instrumentInfo).map(([instrumentId, item]) => {
      const { df, instrument_method } = item;
      return {
        label: `${instrumentMap.get(parseInt(instrumentId)).instrument_name}:`,
        value: `${df.join(", ")}, ${instrument_method}`,
      };
    });
    return (
      <div className="PendingTable__Tooltip__Container">
        {params.map((item, idx) => (
          <div className="PendingTable__Tooltip__Content" key={idx}>
            <label>{item.label}</label>
            <span>{item.value}</span>
          </div>
        ))}
      </div>
    );
  }, [data, instrumentMap]);

  const extendedContext = {
    mrlInstruments,
    testList,
    data,
    chemDropdown,
    loading,
    sampleMap,
    multiSelect,
    extractedColumnMultiSelect,
    enableExtractedColumn,
    handleExtractedCheckboxOnChange,
    selectedGenerate,
    tooltipTitle,
    handleGenistaIdOnClick,
    handleCheckboxOnChange,
    handleSequenceCheckbox,
    // Modal
    mrlTestSet,
    showInstrumentModal: instrumentModalData.open,
    showEditResultModal: editResultModalData.open,
    showDfModal: dfModalData.open,
    modalTest: instrumentModalData.open ? instrumentModalData.modalTest : editResultModalData.modalTest,
    helperFn,
    modalMap,
    selectedIds: dfModalData.selectedIds,
    handleCloseModal,
  };

  if (isGenistaCol) {
    return (
      <td>
        <PendingTableCellContext.Provider value={extendedContext}>
          {multiSelect && (
          <div className="PendingTable__GenistaCell__AnalyzeContainer">
            <div className="PendingTable__GenistaCell__AnalyzeContent">
              <Checkbox
                className="PendingTable__SelectAll"
                disabled={loading || enableExtractedColumn}
                onChange={(e) => handleSelectAll(e, testList)}
                checked={sampleMap.size === testList.length}
              >
                {" "}
                Select All
              </Checkbox>
              <Button
                disabled={sampleMap.size === 0 || enableExtractedColumn}
                loading={loading}
                type="link"
                onClick={handleSubmitButton}
              >
                {sample_status === "analyze" ? "Submit" : "Generate Label"}
              </Button>
            </div>
            <Divider className="PendingTable__CheckboxContainer__Divider" />
          </div>
          )}
          {extractedColumnMultiSelect && enableExtractedColumn && (
          <div className="PendingTable__GenistaCell__AnalyzeContainer">
            <div className="PendingTable__GenistaCell__AnalyzeContent">
              <Checkbox
                className="PendingTable__SelectAll"
                onChange={(e) => handleSequenceSelectAll(e, testList)}
                checked={seqCheckedCount === testList.length}
              >
                {" "}
                Select All
              </Checkbox>
            </div>
            <Divider className="PendingTable__CheckboxContainer__Divider" />
          </div>
          )}
          {useLoadedMrlLayout && (
          <MrlCellContent />
          )}
          {!useLoadedMrlLayout && (
          <GenistaIDList testList={testList} />
          )}
          {instrumentModalData.open && (
          <InstrumentModal />
          )}
          {editResultModalData.open && (
          <EditResultModal />
          )}
          {dfModalData.open && (
          <DfLabelModal />
          )}
        </PendingTableCellContext.Provider>
      </td>
    );
  }

  return (
    <td>
      <Tooltip
        title={dfTooltip()}
        placement="topLeft"
        overlayClassName="PendingTable__Tooltip"
      >
        {data[param] ? data[param] : "-"}
      </Tooltip>
    </td>
  );
};

export default PendingTableCell;
